import { useState, useEffect, useCallback } from 'react'
import * as AlertDialog from '@radix-ui/react-alert-dialog'
import {
  GitBranch,
  Plus,
  Trash2,
  Archive,
  Merge,
  Lock,
  Unlock,
  Loader2,
  Users,
  Clock,
  ChevronDown,
  ChevronRight,
  Terminal,
  ExternalLink,
} from 'lucide-react'
import {
  listAppSessions,
  createAppSession,
  openAppSession,
  lockAppSession,
  unlockAppSession,
  mergeAppSession,
  archiveAppSession,
  deleteAppSession,
  preflightBringChangesBack,
  preflightCloseWorkSession,
  preflightDeleteWorkSession,
  type AppSession,
  type SourcePreflightResponse,
} from '../lib/api'
import { formatRelativeTime } from '../lib/format'
import { withMountBase } from '../lib/mountBase'
import {
  sessionChangedFiles,
  sessionHasUnsavedChanges,
  sessionSavedChanges,
  sessionChangesApplied,
  pluralize,
} from '../lib/sessionHelpers'

interface SessionsPanelProps {
  appName: string
  onOpenSession?: (terminalUrl: string, chromeUrl: string) => void
  /** Parent-provided sessions. When supplied, SessionsPanel skips its internal fetch. */
  sessions?: AppSession[]
  /** Callback asking the parent to re-fetch and update `sessions`. */
  onSessionsRefresh?: () => Promise<void> | void
}

interface PendingConfirmAction {
  title: string
  description: string
  confirmLabel: string
  loadingLabel: string
  variant?: 'default' | 'destructive'
  onConfirm: () => Promise<void>
}

export default function SessionsPanel({
  appName,
  onOpenSession,
  sessions: providedSessions,
  onSessionsRefresh,
}: SessionsPanelProps) {
  const usingProvidedSessions = providedSessions !== undefined
  const [internalSessions, setInternalSessions] = useState<AppSession[]>([])
  const sessions = providedSessions ?? internalSessions
  const [loading, setLoading] = useState(!usingProvidedSessions)
  const [error, setError] = useState<string | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [expandedSession, setExpandedSession] = useState<string | null>(null)
  const [confirmAction, setConfirmAction] = useState<PendingConfirmAction | null>(null)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [confirmError, setConfirmError] = useState<string | null>(null)

  // Clear in-dialog error whenever a new confirm action is opened (D-35).
  useEffect(() => {
    setConfirmError(null)
  }, [confirmAction])

  const loadSessions = useCallback(async () => {
    if (usingProvidedSessions) {
      // Parent owns the fetch lifecycle (D-31 / UX-17 dedup).
      await onSessionsRefresh?.()
      return
    }
    try {
      // Always fetch archived; showArchived only controls display filter (D-10, D-11, D-12).
      const result = await listAppSessions(appName, true)
      setInternalSessions(result.sessions)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load work areas')
    } finally {
      setLoading(false)
    }
  }, [appName, usingProvidedSessions, onSessionsRefresh])

  useEffect(() => {
    loadSessions()
  }, [loadSessions])

  function formatPreflightDescription(
    response: SourcePreflightResponse,
    confirmLabel: string,
  ): string {
    const warnings = response.warnings
      .map((warning) => `- ${warning.message}`)
      .join('\n')
    return [
      response.summary,
      warnings ? `\nPlease note:\n${warnings}` : '',
      '',
      confirmLabel,
    ].filter(Boolean).join('\n')
  }

  async function confirmPreflightAction(
    confirmLabel: string,
    loadPreflight: () => Promise<SourcePreflightResponse>,
    fallbackSummary: string,
  ): Promise<string> {
    try {
      const response = await loadPreflight()
      return formatPreflightDescription(response, confirmLabel)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'The safety check could not run.'
      return [
        fallbackSummary,
        '',
        `Safety check unavailable: ${message}`,
        '',
        confirmLabel,
      ].join('\n')
    }
  }

  async function runConfirmAction() {
    if (!confirmAction) return
    setConfirmLoading(true)
    setConfirmError(null)
    try {
      await confirmAction.onConfirm()
      setConfirmAction(null)
    } catch (err) {
      setConfirmError(err instanceof Error ? err.message : 'Action failed')
      // Keep dialog open so the user can retry or cancel (D-09).
    } finally {
      setConfirmLoading(false)
    }
  }

  async function handleOpenSession(session: AppSession) {
    setActionLoading(`open-${session.id}`)
    try {
      const result = await openAppSession(appName, session.id)
      if (onOpenSession) {
        onOpenSession(result.terminal_url, result.chrome_url)
      } else {
        // Navigate to workspace
        window.location.href = result.terminal_url
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open work area')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleLock(session: AppSession) {
    setError(null)
    setConfirmAction({
      title: session.locked_by
        ? `Release work area "${session.name}"?`
        : `Reserve work area "${session.name}"?`,
      description: session.locked_by
        ? 'Other editors will be able to make changes in it again.'
        : 'This marks it as reserved so other editors know not to change it until you release it.',
      confirmLabel: session.locked_by ? 'Release' : 'Reserve',
      loadingLabel: session.locked_by ? 'Releasing...' : 'Reserving...',
      onConfirm: async () => {
        setActionLoading(`lock-${session.id}`)
        try {
          if (session.locked_by) {
            await unlockAppSession(appName, session.id)
          } else {
            await lockAppSession(appName, session.id)
          }
          await loadSessions()
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to update reservation')
          throw err
        } finally {
          setActionLoading(null)
        }
      },
    })
  }

  async function handleMerge(session: AppSession) {
    setError(null)
    setActionLoading(`preflight-merge-${session.id}`)
    const description = await confirmPreflightAction(
      'Apply changes',
      () => preflightBringChangesBack(appName, session.id),
      'This applies saved changes from this work area to the main app.',
    )
    setActionLoading(null)
    setConfirmAction({
      title: `Apply changes from "${session.name}"?`,
      description,
      confirmLabel: 'Apply changes',
      loadingLabel: 'Applying...',
      onConfirm: async () => {
        setActionLoading(`merge-${session.id}`)
        try {
          const result = await mergeAppSession(appName, session.id, false)
          if (result.success) {
            await loadSessions()
          } else {
            const message = result.conflicts
              ? `${result.message || 'Could not apply changes'}:\n${result.conflicts}`
              : result.message || 'Could not apply changes'
            setError(message)
            throw new Error(message)
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to apply changes')
          throw err
        } finally {
          setActionLoading(null)
        }
      },
    })
  }

  async function handleArchive(session: AppSession) {
    setError(null)
    setActionLoading(`preflight-archive-${session.id}`)
    const description = await confirmPreflightAction(
      'Close work area',
      () => preflightCloseWorkSession(appName, session.id),
      'This keeps the chat history but removes the separate files for this work area.',
    )
    setActionLoading(null)
    setConfirmAction({
      title: `Close work area "${session.name}"?`,
      description,
      confirmLabel: 'Close',
      loadingLabel: 'Closing...',
      onConfirm: async () => {
        setActionLoading(`archive-${session.id}`)
        try {
          await archiveAppSession(appName, session.id)
          await loadSessions()
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to close work area')
          throw err
        } finally {
          setActionLoading(null)
        }
      },
    })
  }

  async function handleDelete(session: AppSession) {
    setError(null)
    setActionLoading(`preflight-delete-${session.id}`)
    const description = await confirmPreflightAction(
      'Delete work area',
      () => preflightDeleteWorkSession(appName, session.id),
      'This removes the separate files for this work area.',
    )
    setActionLoading(null)
    setConfirmAction({
      title: `Delete work area "${session.name}"?`,
      description,
      confirmLabel: 'Delete',
      loadingLabel: 'Deleting...',
      variant: 'destructive',
      onConfirm: async () => {
        setActionLoading(`delete-${session.id}`)
        try {
          await deleteAppSession(appName, session.id)
          await loadSessions()
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to delete work area')
          throw err
        } finally {
          setActionLoading(null)
        }
      },
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const activeSessions = sessions.filter(s => s.status !== 'archived')
  const archivedSessions = sessions.filter(s => s.status === 'archived')

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <GitBranch className="w-5 h-5" />
          Work areas
        </h3>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New work area
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Sessions List */}
      <div className="space-y-2">
        {activeSessions.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No work areas yet</p>
            <p className="text-sm">Create one to try changes separately</p>
          </div>
        ) : (
          activeSessions.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              appName={appName}
              isExpanded={expandedSession === session.id}
              onToggleExpand={() => setExpandedSession(
                expandedSession === session.id ? null : session.id
              )}
              actionLoading={actionLoading}
              onOpen={() => handleOpenSession(session)}
              onLock={() => handleLock(session)}
              onMerge={() => handleMerge(session)}
              onArchive={() => handleArchive(session)}
              onDelete={() => handleDelete(session)}
            />
          ))
        )}
      </div>

      {/* Archived Toggle */}
      {archivedSessions.length > 0 && (
        <div className="pt-4 border-t border-border">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {showArchived ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            <Archive className="w-4 h-4" />
            {archivedSessions.length} closed work {archivedSessions.length === 1 ? 'area' : 'areas'}
          </button>
          {showArchived && (
            <div className="mt-2 space-y-2 pl-6">
              {archivedSessions.map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/50"
                >
                  <div>
                    <div className="font-medium text-muted-foreground">{session.name}</div>
                    <div className="text-xs text-muted-foreground">
                      Closed work area
                    </div>
                  </div>
                  
                    <button title="Delete this work area and its files"
                      onClick={() => handleDelete(session)}
                      disabled={actionLoading === `delete-${session.id}`}
                      className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                    >
                      {actionLoading === `delete-${session.id}` ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <AlertDialog.Root
        open={!!confirmAction}
        onOpenChange={(open) => {
          if (confirmLoading) return
          if (!open) setConfirmAction(null)
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <AlertDialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-6 shadow-lg">
            <AlertDialog.Title className="text-lg font-semibold mb-2">
              {confirmAction?.title}
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-muted-foreground whitespace-pre-wrap mb-4">
              {confirmAction?.description}
            </AlertDialog.Description>
            {confirmError && (
              <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {confirmError}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors disabled:opacity-50"
                  disabled={confirmLoading}
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <button
                type="button"
                onClick={runConfirmAction}
                disabled={confirmLoading}
                className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                  confirmAction?.variant === 'destructive'
                    ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
                }`}
              >
                {confirmLoading
                  ? confirmAction?.loadingLabel
                  : confirmError
                    ? 'Try again'
                    : confirmAction?.confirmLabel}
              </button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateSessionModal
          appName={appName}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false)
            loadSessions()
          }}
        />
      )}
    </div>
  )
}

interface SessionCardProps {
  session: AppSession
  appName: string
  isExpanded: boolean
  onToggleExpand: () => void
  actionLoading: string | null
  onOpen: () => void
  onLock: () => void
  onMerge: () => void
  onArchive: () => void
  onDelete: () => void
}

function SessionCard({
  session,
  appName,
  isExpanded,
  onToggleExpand,
  actionLoading,
  onOpen,
  onLock,
  onMerge,
  onArchive,
  onDelete,
}: SessionCardProps) {
  const isMain = session.name === 'main'
  const isLocked = !!session.locked_by
  const hasPresence = session.presence && session.presence.length > 0
  // Prefer the API-provided preview URL; otherwise build one under the current mount base
  // so it resolves correctly under /a/dev/{app}, /a/branch/{name}/{app}, and /a/{app}/ (D-20, D-21 / UX-10).
  const branchPreviewUrl = !isMain
    ? (session.branch_preview_url ?? withMountBase(`/a/branch/${session.name}/${appName}/`))
    : null
  const changedFiles = sessionChangedFiles(session)
  const savedChanges = sessionSavedChanges(session)
  const hasUnsavedChanges = sessionHasUnsavedChanges(session)
  const changesApplied = sessionChangesApplied(session)

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      {/* Main Row */}
      <div
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-secondary/50 transition-colors"
        onClick={onToggleExpand}
      >
        <div className="flex-shrink-0">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{session.name}</span>
            {isMain && (
              <span className="px-1.5 py-0.5 rounded text-xs bg-primary/10 text-primary">
                main
              </span>
            )}
            {changedFiles > 0 && (
              <span className="rounded px-1.5 py-0.5 text-xs bg-blue-500/10 text-blue-500">
                {pluralize(changedFiles, 'changed file')}
              </span>
            )}
            {savedChanges > 0 && (
              <span className="rounded px-1.5 py-0.5 text-xs bg-green-500/10 text-green-500">
                {pluralize(savedChanges, 'saved change')}
              </span>
            )}
            {hasUnsavedChanges && (
              <span className="rounded px-1.5 py-0.5 text-xs bg-amber-500/10 text-amber-600 dark:text-amber-400">
                Unsaved
              </span>
            )}
            {!isMain && !changesApplied && (
              <span className="rounded px-1.5 py-0.5 text-xs bg-amber-500/10 text-amber-600 dark:text-amber-400">
                Not applied
              </span>
            )}
            {isLocked && (
              <span className="px-1.5 py-0.5 rounded text-xs bg-yellow-500/10 text-yellow-500 flex items-center gap-1">
                <Lock className="w-3 h-3" />
                {session.locked_by_email?.split('@')[0]}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <GitBranch className="w-3 h-3" />
            {isMain ? 'Main app version' : 'Separate work area'}
            {hasPresence && (
              <span className="flex items-center gap-1 text-green-500">
                <Users className="w-3 h-3" />
                {session.presence.length} online
              </span>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onOpen}
            disabled={actionLoading === `open-${session.id}`}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm bg-green-500/10 text-green-500 hover:bg-green-500/20 transition-colors disabled:opacity-50"
          >
            {actionLoading === `open-${session.id}` ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Terminal className="w-4 h-4" />
            )}
            Open
          </button>
          {branchPreviewUrl && (
            <a
              href={branchPreviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="w-4 h-4" />
              Preview
            </a>
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-0 border-t border-border">
          <div className="grid grid-cols-2 gap-4 py-3 text-sm md:grid-cols-4">
            <div>
              <span className="text-muted-foreground">Created</span>
              <div>{session.created_at ? new Date(session.created_at).toLocaleDateString() : 'N/A'}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Last accessed</span>
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {session.last_accessed_at
                  ? formatRelativeTime(session.last_accessed_at)
                  : 'Never'}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">Changed files</span>
              <div>{changedFiles}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Saved changes</span>
              <div>{savedChanges}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Unsaved changes</span>
              <div>{hasUnsavedChanges ? 'Yes' : 'No'}</div>
            </div>
            {!isMain && (
              <div>
                <span className="text-muted-foreground">Applied to main app</span>
                <div>{changesApplied ? 'Up to date' : 'Pending'}</div>
              </div>
            )}
            {branchPreviewUrl && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Preview</span>
                <div className="font-mono text-xs flex items-center gap-2">
                  <a href={branchPreviewUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                    Open preview
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-2 border-t border-border">
            
              <button title={isLocked ? "Release this work area" : "Reserve this work area"}
                onClick={onLock}
                disabled={!!actionLoading}
                className="flex items-center gap-1.5 px-2 py-1 rounded text-xs hover:bg-secondary transition-colors disabled:opacity-50"
              >
                {isLocked ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                {isLocked ? 'Release' : 'Reserve'}
              </button>
            

            {!isMain && (
              <>
                
                  <button title="Apply this work area's saved changes to the main app"
                    onClick={onMerge}
                    disabled={!!actionLoading}
                    className="flex items-center gap-1.5 px-2 py-1 rounded text-xs hover:bg-secondary transition-colors disabled:opacity-50"
                  >
                    {actionLoading === `preflight-merge-${session.id}` ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <Merge className="w-3 h-3" />
                        Apply changes
                      </>
                    )}
                  </button>
                

                
                  <button title="Close this work area"
                    onClick={onArchive}
                    disabled={!!actionLoading}
                    className="flex items-center gap-1.5 px-2 py-1 rounded text-xs hover:bg-secondary transition-colors disabled:opacity-50"
                  >
                    {actionLoading === `preflight-archive-${session.id}` ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <Archive className="w-3 h-3" />
                        Close
                      </>
                    )}
                  </button>
                

                
                  <button title="Delete this work area and its files"
                    onClick={onDelete}
                    disabled={!!actionLoading}
                    className="flex items-center gap-1.5 px-2 py-1 rounded text-xs text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                  >
                    {actionLoading === `preflight-delete-${session.id}` ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </>
                    )}
                  </button>
                
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function CreateSessionModal({
  appName,
  onClose,
  onCreated,
}: {
  appName: string
  onClose: () => void
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return

    setCreating(true)
    setError(null)

    try {
      await createAppSession(appName, name.trim().toLowerCase())
      onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create work area')
    } finally {
      setCreating(false)
    }
  }

  const isValidName = /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(name)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-md p-6">
        <h2 className="text-xl font-semibold mb-1">New work area</h2>
        <p className="text-sm text-muted-foreground mb-6">
          Create a separate place to try changes before bringing them back.
        </p>

        <form onSubmit={handleCreate}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Work area name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase())}
              placeholder="e.g., fix-login-bug"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
            />
            {name && !isValidName && (
              <p className="text-xs text-destructive mt-1">
                Use lowercase letters, numbers, and hyphens only. Must start and end with alphanumeric.
              </p>
            )}
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm mb-4">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating || !name.trim() || !isValidName}
              className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
